import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import ActividadesEspecificasSelector from "../components/ActividadesEspecificasSelector";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter,
  DialogClose
} from "../components/ui/dialog";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { 
  Users, 
  FileText,
  Plus,
  Pencil,
  Trash2,
  Search,
  RefreshCw,
  Download,
  FileSpreadsheet,
  FileJson,
  FileType,
  File,
  Check,
  ChevronsUpDown,
  X,
  MapPin
} from "lucide-react";
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { saveAs } from 'file-saver';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';

// Fix for default marker icon in Leaflet with React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Componente para manejar clics en el mapa
const LocationMarker = ({ position, setPosition, setFormData, formData }) => {
  useMapEvents({
    click(e) {
      const { lat, lng } = e.latlng;
      setPosition([lat, lng]);
      setFormData({
        ...formData,
        ubicacion_gps: `${lat.toFixed(6)}, ${lng.toFixed(6)}`
      });
    },
  });

  return position ? <Marker position={position} /> : null;
};

// Coordenadas de La Asunción, Nueva Esparta, Venezuela
const LA_ASUNCION_COORDS = [11.0333, -63.8625];

const submoduloConfig = {
  "con-lae": { title: "Contribuyente con LAE", icon: FileText, color: "green" },
  "sin-lae": { title: "Contribuyente sin LAE", icon: Users, color: "cyan" }
};

const ContribuyentesPage = () => {
  const { submodulo } = useParams();
  const navigate = useNavigate();
  const { API, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [conLaeFilters, setConLaeFilters] = useState({ nombre: "", rif: "", denominacion: "", numero_lae: "", tipo_licencia: "", actividades_eco: "", codigo_catastral: "", estado: "" });
  const [sinLaeFilters, setSinLaeFilters] = useState({ nombre: "", rif: "", domicilio: "", correo: "", estado: "" });
  const [data, setData] = useState([]);
  // Paginación
  const [currentPage, setCurrentPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize] = useState(50);
  const [showAll, setShowAll] = useState(false);
  const [serverSearch, setServerSearch] = useState("");

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [tiposImpuesto, setTiposImpuesto] = useState([]);
  const [tiposImpuestoOpen, setTiposImpuestoOpen] = useState(false);
  const [tiposImpuestoSearch, setTiposImpuestoSearch] = useState("");
  // Map state
  const [mapPosition, setMapPosition] = useState(null);
  // Actividades económicas states
  const [actividadesEconomicas, setActividadesEconomicas] = useState([]);
  const [actividadesOpen, setActividadesOpen] = useState(false);
  const [actividadesSearch, setActividadesSearch] = useState("");
  // Obligaciones (Solvencia + Conformidad)
  const [obligacionesMap, setObligacionesMap] = useState({ mapa_conformidades: {}, mapa_solvencias: {} });
  const [sincronizando, setSincronizando] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };
  const currentConfig = submoduloConfig[submodulo] || null;

  // Buscar ficha catastral por código y auto-llenar campos
  const fetchFichaPorCodigoCatastral = async (codigo) => {
    if (!codigo || codigo.trim() === "") {
      setFormData(prev => ({ ...prev, rif_catastro: "", propietario_catastro: "" }));
      return;
    }
    try {
      const response = await axios.get(`${API}/catastro/ficha-catastral-por-codigo/${encodeURIComponent(codigo)}`, { headers });
      setFormData(prev => ({
        ...prev,
        rif_catastro: response.data.rif || "",
        propietario_catastro: response.data.nombre_razon_social || ""
      }));
    } catch {
      setFormData(prev => ({ ...prev, rif_catastro: "", propietario_catastro: "" }));
    }
  };

  // Fetch tipos de impuesto
  const fetchTiposImpuesto = async () => {
    try {
      const response = await axios.get(`${API}/definiciones/tipos-impuesto`, { headers });
      setTiposImpuesto(response.data);
    } catch (error) {
      console.error("Error fetching tipos de impuesto:", error);
    }
  };

  // Fetch actividades económicas
  const fetchActividadesEconomicas = async () => {
    try {
      const response = await axios.get(`${API}/definiciones/actividades-economicas`, { headers });
      setActividadesEconomicas(response.data);
    } catch (error) {
      console.error("Error fetching actividades económicas:", error);
    }
  };

  // Fetch data based on submodulo with pagination
  const fetchData = async (page, search, loadAll) => {
    if (!submodulo) return;
    const p = page !== undefined ? page : currentPage;
    const s = search !== undefined ? search : serverSearch;
    const a = loadAll !== undefined ? loadAll : showAll;
    setLoading(true);
    try {
      const endpoint = getEndpoint(submodulo);
      const params = new URLSearchParams();
      if (a) {
        params.set("all", "true");
      } else {
        params.set("page", p.toString());
        params.set("limit", pageSize.toString());
      }
      if (s) params.set("search", s);
      
      const response = await axios.get(`${API}/contribuyentes/${endpoint}?${params.toString()}`, { headers });
      
      if (response.data.data) {
        setData(response.data.data);
        setTotalRecords(response.data.total);
      } else {
        setData(response.data);
        setTotalRecords(response.data.length);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error al cargar los datos");
    }
    setLoading(false);
  };

  const fetchObligaciones = async () => {
    if (submodulo !== "con-lae") return;
    try {
      const response = await axios.get(`${API}/contribuyentes/con-lae/resumen-obligaciones`, { headers });
      setObligacionesMap({
        mapa_conformidades: response.data.mapa_conformidades || {},
        mapa_solvencias: response.data.mapa_solvencias || {}
      });
    } catch (error) {
      console.error("Error fetching obligaciones:", error);
    }
  };

  const handleSincronizar = async () => {
    setSincronizando(true);
    try {
      const response = await axios.post(`${API}/contribuyentes/con-lae/sincronizar-obligaciones`, {}, { headers });
      const d = response.data;
      toast.success(`Sincronización completada: ${d.conformidades_creadas} conformidades y ${d.solvencias_creadas} solvencias creadas`);
      fetchObligaciones();
    } catch (error) {
      toast.error("Error al sincronizar obligaciones");
    }
    setSincronizando(false);
  };

  useEffect(() => {
    setCurrentPage(0);
    setServerSearch("");
    setShowAll(false);
    fetchData(0, "", false);
    fetchTiposImpuesto();
    fetchActividadesEconomicas();
    fetchObligaciones();
  }, [submodulo]);

  // CRUD operations
  const handleCreate = async () => {
    // Validación campos obligatorios para sin LAE
    if (submodulo === "sin-lae") {
      if (!formData.nombre) { toast.error("El campo Nombre y Apellido / Razón Social es obligatorio"); return; }
      if (!formData.tipo_rif) { toast.error("El campo Tipo RIF es obligatorio"); return; }
      if (!formData.rif) { toast.error("El campo RIF o Cédula es obligatorio"); return; }
      if (!formData.domicilio_fiscal) { toast.error("El campo Domicilio Fiscal es obligatorio"); return; }
      if (!formData.correo_electronico) { toast.error("El campo Correo Electrónico es obligatorio"); return; }
    }
    try {
      const endpoint = getEndpoint(submodulo);
      await axios.post(`${API}/contribuyentes/${endpoint}`, formData, { headers });
      toast.success("Registro creado exitosamente");
      setDialogOpen(false);
      setFormData({});
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear registro");
    }
  };

  const handleUpdate = async () => {
    // Validación campos obligatorios para sin LAE
    if (submodulo === "sin-lae") {
      if (!formData.nombre) { toast.error("El campo Nombre y Apellido / Razón Social es obligatorio"); return; }
      if (!formData.tipo_rif) { toast.error("El campo Tipo RIF es obligatorio"); return; }
      if (!formData.rif) { toast.error("El campo RIF o Cédula es obligatorio"); return; }
      if (!formData.domicilio_fiscal) { toast.error("El campo Domicilio Fiscal es obligatorio"); return; }
      if (!formData.correo_electronico) { toast.error("El campo Correo Electrónico es obligatorio"); return; }
    }
    try {
      const endpoint = getEndpoint(submodulo);
      await axios.put(`${API}/contribuyentes/${endpoint}/${editingItem.id}`, formData, { headers });
      toast.success("Registro actualizado exitosamente");
      setDialogOpen(false);
      setEditingItem(null);
      setFormData({});
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar registro");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Está seguro de eliminar este registro?")) return;
    try {
      const endpoint = getEndpoint(submodulo);
      await axios.delete(`${API}/contribuyentes/${endpoint}/${id}`, { headers });
      toast.success("Registro eliminado exitosamente");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar registro");
    }
  };

  const getEndpoint = (sub) => {
    const endpoints = {
      "con-lae": "con-lae",
      "sin-lae": "sin-lae"
    };
    return endpoints[sub];
  };

  const openCreateDialog = () => {
    setEditingItem(null);
    setFormData({});
    setMapPosition(null);
    setDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setFormData(item);
    // Auto-llenar campos de catastro si tiene código y faltan datos
    if (item.codigo_catastral && (!item.rif_catastro || !item.propietario_catastro)) {
      fetchFichaPorCodigoCatastral(item.codigo_catastral);
    }
    // Parse existing GPS coordinates if available
    if (item.ubicacion_gps) {
      const coords = item.ubicacion_gps.split(',').map(c => parseFloat(c.trim()));
      if (coords.length === 2 && !isNaN(coords[0]) && !isNaN(coords[1])) {
        setMapPosition(coords);
      } else {
        setMapPosition(null);
      }
    } else {
      setMapPosition(null);
    }
    setDialogOpen(true);
  };

  // Filter data
  const filteredData = data.filter(item => {
    if (submodulo === "con-lae") {
      const f = conLaeFilters;
      if (f.nombre && !(item.nombre || "").toLowerCase().includes(f.nombre.toLowerCase())) return false;
      if (f.rif && !(`${item.tipo_rif || "J"}-${item.rif}` || "").toLowerCase().includes(f.rif.toLowerCase())) return false;
      if (f.denominacion && !(item.denominacion_comercial || "").toLowerCase().includes(f.denominacion.toLowerCase())) return false;
      if (f.numero_lae && !(item.numero_lae || "").toLowerCase().includes(f.numero_lae.toLowerCase())) return false;
      if (f.tipo_licencia && f.tipo_licencia !== "todos" && (item.tipo_licencia || "") !== f.tipo_licencia) return false;
      if (f.actividades_eco) {
        const acts = item.actividades_economicas || [];
        const actsInfo = acts.map(actId => {
          const act = actividadesEconomicas.find(a => a.id === actId);
          return act ? `${act.codigo} ${act.nombre} ${act.alicuota}%` : "";
        }).join(" ").toLowerCase();
        if (!actsInfo.includes(f.actividades_eco.toLowerCase())) return false;
      }
      if (f.codigo_catastral && !(item.codigo_catastral || "").toLowerCase().includes(f.codigo_catastral.toLowerCase())) return false;
      if (f.estado && f.estado !== "todos") {
        const isActive = item.is_active !== false;
        if ((f.estado === "activo") !== isActive) return false;
      }
      return true;
    }
    if (submodulo === "sin-lae") {
      const f = sinLaeFilters;
      if (f.nombre && !(item.nombre || "").toLowerCase().includes(f.nombre.toLowerCase())) return false;
      if (f.rif && !(`${item.tipo_rif || "V"}-${item.rif}` || "").toLowerCase().includes(f.rif.toLowerCase())) return false;
      if (f.domicilio && !(item.domicilio_fiscal || "").toLowerCase().includes(f.domicilio.toLowerCase())) return false;
      if (f.correo && !(item.correo_electronico || "").toLowerCase().includes(f.correo.toLowerCase())) return false;
      if (f.estado && f.estado !== "todos") {
        const isActive = item.is_active !== false;
        if ((f.estado === "activo") !== isActive) return false;
      }
      return true;
    }
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      item.nombre?.toLowerCase().includes(searchLower) ||
      item.cedula?.toLowerCase().includes(searchLower) ||
      item.rif?.toLowerCase().includes(searchLower) ||
      item.razon_social?.toLowerCase().includes(searchLower)
    );
  });

  // Render form fields based on submodulo
  const renderFormFields = () => {
    switch (submodulo) {
      case "con-lae":
        return (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            {/* Datos Básicos */}
            <div className="border-b pb-3 mb-3">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">Datos Básicos</h4>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>Nombre / Razón Social</Label>
                  <Input 
                    value={formData.nombre || ""} 
                    onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                    placeholder="Nombre del contribuyente"
                    data-testid="form-nombre"
                  />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="space-y-2">
                    <Label>Tipo RIF</Label>
                    <Select 
                      value={formData.tipo_rif || "J"} 
                      onValueChange={(value) => setFormData({...formData, tipo_rif: value})}
                    >
                      <SelectTrigger data-testid="form-tipo-rif">
                        <SelectValue placeholder="Tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="V">V</SelectItem>
                        <SelectItem value="E">E</SelectItem>
                        <SelectItem value="J">J</SelectItem>
                        <SelectItem value="G">G</SelectItem>
                        <SelectItem value="P">P</SelectItem>
                        <SelectItem value="C">C</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label>RIF</Label>
                    <Input 
                      value={formData.rif || ""} 
                      onChange={(e) => setFormData({...formData, rif: e.target.value})}
                      placeholder="Ej: 12345678-9"
                      data-testid="form-rif"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Número de LAE</Label>
                    <Input 
                      value={formData.numero_lae || ""} 
                      onChange={(e) => setFormData({...formData, numero_lae: e.target.value})}
                      placeholder="Número de licencia"
                      data-testid="form-numero-lae"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Tipo de Licencia</Label>
                    <Select 
                      value={formData.tipo_licencia || ""} 
                      onValueChange={(value) => setFormData({...formData, tipo_licencia: value})}
                    >
                      <SelectTrigger data-testid="form-tipo-licencia">
                        <SelectValue placeholder="Seleccione tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Permanente">Permanente</SelectItem>
                        <SelectItem value="Transeúnte">Transeúnte</SelectItem>
                        <SelectItem value="Temporal">Temporal</SelectItem>
                        <SelectItem value="No Comercial">No Comercial</SelectItem>
                        <SelectItem value="Temporalmente sin Actividad">Temporalmente sin Actividad</SelectItem>
                        <SelectItem value="Sin Licencia">Sin Licencia</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Tipo de Contribuyente</Label>
                    <Select 
                      value={formData.tipo_contribuyente || ""} 
                      onValueChange={(value) => setFormData({...formData, tipo_contribuyente: value})}
                    >
                      <SelectTrigger data-testid="form-tipo-contribuyente">
                        <SelectValue placeholder="Seleccione tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Firma Personal Venezolano">Firma Personal Venezolano</SelectItem>
                        <SelectItem value="Firma Personal Extranjero">Firma Personal Extranjero</SelectItem>
                        <SelectItem value="Jurídico">Jurídico</SelectItem>
                        <SelectItem value="Gubernamental">Gubernamental</SelectItem>
                        <SelectItem value="Emprendedor">Emprendedor</SelectItem>
                        <SelectItem value="Sucesión">Sucesión</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Denominación Comercial</Label>
                    <Input 
                      value={formData.denominacion_comercial || ""} 
                      onChange={(e) => setFormData({...formData, denominacion_comercial: e.target.value})}
                      placeholder="Nombre comercial"
                      data-testid="form-denominacion"
                    />
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="declarar_en_linea"
                    checked={formData.declarar_en_linea || false}
                    onChange={(e) => setFormData({...formData, declarar_en_linea: e.target.checked})}
                    className="h-4 w-4 rounded border-gray-300"
                    data-testid="form-declarar-linea"
                  />
                  <Label htmlFor="declarar_en_linea">Declarar en Línea</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="activar_prelacion_extranet"
                    checked={formData.activar_prelacion_extranet || false}
                    onChange={(e) => setFormData({...formData, activar_prelacion_extranet: e.target.checked})}
                    className="h-4 w-4 rounded border-gray-300"
                    data-testid="form-prelacion-extranet"
                  />
                  <Label htmlFor="activar_prelacion_extranet">Activar prelación act. Eco. Extranet</Label>
                </div>
                
                {/* Último Trimestre Publicidad Pago */}
                <div className="border rounded-lg p-3 bg-slate-50">
                  <h5 className="text-sm font-medium text-slate-700 mb-3">Último Trimestre Publicidad Pago</h5>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Trimestre</Label>
                      <Select
                        value={formData.trimestre_publicidad || ""}
                        onValueChange={(value) => setFormData({...formData, trimestre_publicidad: value})}
                      >
                        <SelectTrigger data-testid="form-trimestre-publicidad">
                          <SelectValue placeholder="Seleccione trimestre" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1ER">1ER</SelectItem>
                          <SelectItem value="2DO">2DO</SelectItem>
                          <SelectItem value="3ER">3ER</SelectItem>
                          <SelectItem value="4TO">4TO</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Año</Label>
                      <Input 
                        type="number"
                        value={formData.anio_publicidad || ""} 
                        onChange={(e) => setFormData({...formData, anio_publicidad: e.target.value})}
                        placeholder="Ej: 2025"
                        min="2000"
                        max="2100"
                        data-testid="form-anio-publicidad"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Mes/Año Última Declaración</Label>
                    <Input 
                      type="month"
                      value={formData.mes_anio_ultima_declaracion || ""} 
                      onChange={(e) => setFormData({...formData, mes_anio_ultima_declaracion: e.target.value})}
                      data-testid="form-mes-anio-declaracion"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Código Catastral</Label>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="sin-codigo-catastral"
                          checked={formData.sin_codigo_catastral || false}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setFormData({...formData, sin_codigo_catastral: true, codigo_catastral: "No Aplica", rif_catastro: "No Aplica", propietario_catastro: "No Aplica"});
                            } else {
                              setFormData({...formData, sin_codigo_catastral: false, codigo_catastral: "", rif_catastro: "", propietario_catastro: ""});
                            }
                          }}
                          data-testid="check-sin-codigo-catastral"
                        />
                        <Label htmlFor="sin-codigo-catastral" className="text-xs text-slate-500 cursor-pointer">Sin Código Catastral</Label>
                      </div>
                    </div>
                    <Input 
                      value={formData.codigo_catastral || ""} 
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData({...formData, codigo_catastral: val});
                      }}
                      onBlur={(e) => {
                        if (!formData.sin_codigo_catastral) fetchFichaPorCodigoCatastral(e.target.value);
                      }}
                      placeholder="Ej: 1-006862"
                      readOnly={formData.sin_codigo_catastral || false}
                      className={formData.sin_codigo_catastral ? "bg-slate-50" : ""}
                      data-testid="form-codigo-catastral"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>RIF del Catastro</Label>
                      <Input 
                        value={formData.rif_catastro || ""} 
                        readOnly
                        className="bg-slate-50"
                        placeholder="Se llena automáticamente"
                        data-testid="form-rif-catastro"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Propietario del Catastro</Label>
                      <Input 
                        value={formData.propietario_catastro || ""} 
                        readOnly
                        className="bg-slate-50"
                        placeholder="Se llena automáticamente"
                        data-testid="form-propietario-catastro"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contacto */}
            <div className="border-b pb-3 mb-3">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">Contacto</h4>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>Correo Electrónico</Label>
                  <Input 
                    type="email"
                    value={formData.correo_electronico || ""} 
                    onChange={(e) => setFormData({...formData, correo_electronico: e.target.value})}
                    placeholder="correo@ejemplo.com"
                    data-testid="form-correo"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Número Local</Label>
                    <Input 
                      value={formData.numero_local || ""} 
                      onChange={(e) => setFormData({...formData, numero_local: e.target.value})}
                      placeholder="Ej: 0212-1234567"
                      data-testid="form-numero-local"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Celular</Label>
                    <Input 
                      value={formData.celular || ""} 
                      onChange={(e) => setFormData({...formData, celular: e.target.value})}
                      placeholder="Ej: 0414-1234567"
                      data-testid="form-celular"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Ubicación */}
            <div className="border-b pb-3 mb-3">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">Ubicación</h4>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Estado</Label>
                    <Input 
                      value={formData.estado || ""} 
                      onChange={(e) => setFormData({...formData, estado: e.target.value})}
                      placeholder="Estado"
                      data-testid="form-estado"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Municipio</Label>
                    <Input 
                      value={formData.municipio || ""} 
                      onChange={(e) => setFormData({...formData, municipio: e.target.value})}
                      placeholder="Municipio"
                      data-testid="form-municipio"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Parroquia</Label>
                    <Input 
                      value={formData.parroquia || ""} 
                      onChange={(e) => setFormData({...formData, parroquia: e.target.value})}
                      placeholder="Parroquia"
                      data-testid="form-parroquia"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Sector</Label>
                    <Input 
                      value={formData.sector || ""} 
                      onChange={(e) => setFormData({...formData, sector: e.target.value})}
                      placeholder="Sector"
                      data-testid="form-sector"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Domicilio Fiscal</Label>
                  <Input 
                    value={formData.domicilio_fiscal || ""} 
                    onChange={(e) => setFormData({...formData, domicilio_fiscal: e.target.value})}
                    placeholder="Dirección completa"
                    data-testid="form-domicilio"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Punto de Referencia</Label>
                  <Input 
                    value={formData.punto_referencia || ""} 
                    onChange={(e) => setFormData({...formData, punto_referencia: e.target.value})}
                    placeholder="Punto de referencia"
                    data-testid="form-punto-referencia"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Ubicación GPS
                  </Label>
                  <Input 
                    value={formData.ubicacion_gps || ""} 
                    onChange={(e) => {
                      setFormData({...formData, ubicacion_gps: e.target.value});
                      // Parse and update map position
                      const coords = e.target.value.split(',').map(c => parseFloat(c.trim()));
                      if (coords.length === 2 && !isNaN(coords[0]) && !isNaN(coords[1])) {
                        setMapPosition(coords);
                      }
                    }}
                    placeholder="Latitud, Longitud (Ej: 11.0333, -63.8625)"
                    data-testid="form-ubicacion-gps"
                  />
                  <p className="text-xs text-slate-500">Haga clic en el mapa para seleccionar la ubicación</p>
                  <div className="h-64 w-full rounded-lg overflow-hidden border border-slate-200">
                    <MapContainer 
                      center={mapPosition || LA_ASUNCION_COORDS} 
                      zoom={13} 
                      style={{ height: '100%', width: '100%' }}
                      key={mapPosition ? mapPosition.join(',') : 'default'}
                    >
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      <LocationMarker 
                        position={mapPosition} 
                        setPosition={setMapPosition}
                        setFormData={setFormData}
                        formData={formData}
                      />
                    </MapContainer>
                  </div>
                </div>
              </div>
            </div>

            {/* Representante Legal */}
            <div className="border-b pb-3 mb-3">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">Representante Legal</h4>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>Cédula Representante Legal</Label>
                  <Input 
                    value={formData.cedula_representante_legal || ""} 
                    onChange={(e) => setFormData({...formData, cedula_representante_legal: e.target.value})}
                    placeholder="Ej: V-12345678"
                    data-testid="form-cedula-rep"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nombre y Apellido Representante Legal</Label>
                  <Input 
                    value={formData.nombre_apellido_representante_legal || ""} 
                    onChange={(e) => setFormData({...formData, nombre_apellido_representante_legal: e.target.value})}
                    placeholder="Nombre completo"
                    data-testid="form-nombre-rep"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Celular Representante Legal</Label>
                  <Input 
                    value={formData.celular_representante_legal || ""} 
                    onChange={(e) => setFormData({...formData, celular_representante_legal: e.target.value})}
                    placeholder="Ej: 0414-1234567"
                    data-testid="form-celular-rep"
                  />
                </div>
              </div>
            </div>

            {/* Actividades Económicas */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-slate-700">Actividades Económicas</h4>
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                  {(formData.actividades_economicas || []).length} seleccionadas
                </Badge>
              </div>
              <Popover open={actividadesOpen} onOpenChange={setActividadesOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={actividadesOpen}
                    className="w-full justify-between h-auto min-h-10 py-2"
                    data-testid="actividades-trigger"
                  >
                    <div className="flex flex-wrap gap-1 flex-1 text-left">
                      {(formData.actividades_economicas || []).length === 0 ? (
                        <span className="text-slate-500">Seleccione actividades económicas...</span>
                      ) : (
                        (formData.actividades_economicas || []).map(actId => {
                          const actividad = actividadesEconomicas.find(a => a.id === actId);
                          return actividad ? (
                            <Badge 
                              key={actId} 
                              variant="secondary" 
                              className="bg-green-100 text-green-800 hover:bg-green-200 max-w-full"
                            >
                              <span className="truncate max-w-[280px] inline-block align-middle">{actividad.codigo} - {actividad.nombre}</span>
                              <button
                                type="button"
                                className="ml-1 hover:text-green-900"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setFormData({
                                    ...formData, 
                                    actividades_economicas: (formData.actividades_economicas || []).filter(a => a !== actId)
                                  });
                                }}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          ) : null;
                        })
                      )}
                    </div>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <div className="p-2 border-b">
                    <div className="flex items-center gap-2 px-2">
                      <Search className="h-4 w-4 text-slate-400" />
                      <Input
                        placeholder="Buscar actividad económica..."
                        value={actividadesSearch}
                        onChange={(e) => setActividadesSearch(e.target.value)}
                        className="border-0 p-0 h-8 focus-visible:ring-0 focus-visible:ring-offset-0"
                        data-testid="actividades-search"
                      />
                    </div>
                  </div>
                  <div className="max-h-60 overflow-y-auto p-1">
                    {actividadesEconomicas.length === 0 ? (
                      <p className="text-sm text-slate-500 italic text-center py-4">
                        No hay actividades económicas disponibles.
                      </p>
                    ) : (
                      actividadesEconomicas
                        .filter(act => 
                          act.codigo?.toLowerCase().includes(actividadesSearch.toLowerCase()) ||
                          act.nombre?.toLowerCase().includes(actividadesSearch.toLowerCase()) ||
                          act.ramo?.toLowerCase().includes(actividadesSearch.toLowerCase())
                        )
                        .map((actividad) => {
                          const isSelected = (formData.actividades_economicas || []).includes(actividad.id);
                          return (
                            <div
                              key={actividad.id}
                              className={`flex items-center gap-2 px-2 py-2 rounded cursor-pointer transition-colors ${
                                isSelected ? 'bg-green-50' : 'hover:bg-slate-100'
                              }`}
                              onClick={() => {
                                const currentActs = formData.actividades_economicas || [];
                                if (isSelected) {
                                  setFormData({...formData, actividades_economicas: currentActs.filter(a => a !== actividad.id)});
                                } else {
                                  setFormData({...formData, actividades_economicas: [...currentActs, actividad.id]});
                                }
                              }}
                              data-testid={`actividad-option-${actividad.id}`}
                            >
                              <div className={`flex items-center justify-center w-4 h-4 rounded border ${
                                isSelected ? 'bg-green-600 border-green-600' : 'border-slate-300'
                              }`}>
                                {isSelected && <Check className="h-3 w-3 text-white" />}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-mono text-xs bg-slate-100 px-1 rounded">{actividad.codigo}</span>
                                  <span className="font-medium text-sm">{actividad.nombre}</span>
                                </div>
                                <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                                  <span>{actividad.sector}</span>
                                  <span>•</span>
                                  <span>{actividad.ramo}</span>
                                  <span>•</span>
                                  <span className="text-blue-600">{actividad.alicuota}%</span>
                                  <span>•</span>
                                  <span>Min: {actividad.minimo_tributable}</span>
                                </div>
                              </div>
                            </div>
                          );
                        })
                    )}
                    {actividadesEconomicas.length > 0 && 
                     actividadesEconomicas.filter(act => 
                       act.codigo?.toLowerCase().includes(actividadesSearch.toLowerCase()) ||
                       act.nombre?.toLowerCase().includes(actividadesSearch.toLowerCase()) ||
                       act.ramo?.toLowerCase().includes(actividadesSearch.toLowerCase())
                     ).length === 0 && (
                      <p className="text-sm text-slate-500 italic text-center py-4">
                        No se encontraron resultados para "{actividadesSearch}"
                      </p>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            {/* Actividad Económica Específica (giro comercial concreto) */}
            <div className="md:col-span-2">
              <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">
                Actividad Económica Específica
                <span className="text-xs font-normal text-slate-500 ml-2">
                  (giro comercial del establecimiento — multi-selección con búsqueda)
                </span>
              </Label>
              <ActividadesEspecificasSelector
                value={formData.actividades_economicas_especificas || []}
                onChange={(arr) => setFormData({ ...formData, actividades_economicas_especificas: arr })}
                placeholder="Ej: Panadería, Restaurante, Ferretería..."
                testid="form-actividad-especifica"
              />
            </div>

          </div>
        );

      case "sin-lae":
        return (
          <>
            <div className="space-y-2">
              <Label>Nombre y apellido / Razón Social</Label>
              <Input 
                value={formData.nombre || ""} 
                onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                placeholder="Nombre del contribuyente"
                data-testid="form-nombre"
              />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-2">
                <Label>Tipo RIF</Label>
                <Select 
                  value={formData.tipo_rif || "V"} 
                  onValueChange={(value) => setFormData({...formData, tipo_rif: value})}
                >
                  <SelectTrigger data-testid="form-tipo-rif">
                    <SelectValue placeholder="Tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="V">V</SelectItem>
                    <SelectItem value="E">E</SelectItem>
                    <SelectItem value="J">J</SelectItem>
                    <SelectItem value="G">G</SelectItem>
                    <SelectItem value="P">P</SelectItem>
                    <SelectItem value="C">C</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 col-span-2">
                <Label>RIF o Cédula</Label>
                <Input 
                  value={formData.rif || ""} 
                  onChange={(e) => setFormData({...formData, rif: e.target.value})}
                  placeholder="Ej: 12345678-9"
                  data-testid="form-rif"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Domicilio Fiscal</Label>
              <Input 
                value={formData.domicilio_fiscal || ""} 
                onChange={(e) => setFormData({...formData, domicilio_fiscal: e.target.value})}
                placeholder="Dirección completa"
                data-testid="form-domicilio"
              />
            </div>
            <div className="space-y-2">
              <Label>Correo Electrónico</Label>
              <Input 
                type="email"
                value={formData.correo_electronico || ""} 
                onChange={(e) => setFormData({...formData, correo_electronico: e.target.value})}
                placeholder="correo@ejemplo.com"
                data-testid="form-correo"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Teléfono Local</Label>
                <Input 
                  value={formData.telefono_local || ""} 
                  onChange={(e) => setFormData({...formData, telefono_local: e.target.value})}
                  placeholder="Ej: 0212-1234567"
                  data-testid="form-telefono-local"
                />
              </div>
              <div className="space-y-2">
                <Label>Celular</Label>
                <Input 
                  value={formData.celular || ""} 
                  onChange={(e) => setFormData({...formData, celular: e.target.value})}
                  placeholder="Ej: 0414-1234567"
                  data-testid="form-celular"
                />
              </div>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  // Get table columns based on submodulo
  const getTableColumns = () => {
    switch (submodulo) {
      case "con-lae":
        return [
          { key: "nombre", header: "Razón Social" },
          { key: "denominacion_comercial", header: "Denominación Comercial" },
          { key: "rif", header: "RIF", render: (item) => (
            <span className="font-mono">{item.tipo_rif || "J"}-{item.rif}</span>
          )},
          { key: "numero_lae", header: "N° LAE" },
          { key: "tipo_licencia", header: "Tipo Licencia", render: (item) => (
            <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
              {item.tipo_licencia || "-"}
            </Badge>
          )},
          { key: "actividades_economicas", header: "Actividades Eco.", render: (item) => {
            const acts = item.actividades_economicas || [];
            if (acts.length === 0) return <span className="text-slate-400">-</span>;
            const actividadesInfo = acts.map(actId => {
              const act = actividadesEconomicas.find(a => a.id === actId);
              return act ? { codigo: act.codigo, nombre: act.nombre, alicuota: act.alicuota } : null;
            }).filter(Boolean);
            return (
              <div className="flex flex-wrap gap-1">
                {actividadesInfo.slice(0, 2).map((act, idx) => (
                  <Badge key={idx} variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">
                    {act.codigo} ({act.alicuota}%)
                  </Badge>
                ))}
                {actividadesInfo.length > 2 && (
                  <Badge variant="outline" className="bg-slate-100 text-slate-600 border-slate-200 text-xs">
                    +{actividadesInfo.length - 2}
                  </Badge>
                )}
              </div>
            );
          }},
          { key: "actividades_economicas_especificas", header: "Actividad Específica", render: (item) => {
            const arr = item.actividades_economicas_especificas || [];
            if (arr.length === 0) return <span className="text-slate-400">-</span>;
            return (
              <div className="flex flex-wrap gap-1 max-w-[220px]">
                {arr.slice(0, 2).map((nombre, idx) => (
                  <Badge key={idx} variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 text-xs">
                    {nombre}
                  </Badge>
                ))}
                {arr.length > 2 && (
                  <Badge variant="outline" className="bg-slate-100 text-slate-600 border-slate-200 text-xs">
                    +{arr.length - 2}
                  </Badge>
                )}
              </div>
            );
          }},
          { key: "mes_anio_ultima_declaracion", header: "Últ. Declaración", render: (item) => {
            if (!item.mes_anio_ultima_declaracion) return <span className="text-slate-400">-</span>;
            const [year, month] = item.mes_anio_ultima_declaracion.split('-');
            const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
            return <span className="font-mono text-sm">{meses[parseInt(month) - 1]} {year}</span>;
          }},
          { key: "codigo_catastral", header: "Cód. Catastral", render: (item) => (
            item.codigo_catastral ? <span className="font-mono text-sm">{item.codigo_catastral}</span> : <span className="text-slate-400">-</span>
          )},
          { key: "rif_catastro", header: "RIF Catastro", render: (item) => (
            item.rif_catastro ? <span className="font-mono text-sm">{item.rif_catastro}</span> : <span className="text-slate-400">-</span>
          )},
          { key: "propietario_catastro", header: "Propietario Catastro", render: (item) => (
            item.propietario_catastro ? <span className="text-sm">{item.propietario_catastro}</span> : <span className="text-slate-400">-</span>
          )},
          { key: "ubicacion_gps", header: "Ubicación GPS", render: (item) => (
            item.ubicacion_gps ? <span className="font-mono text-sm">{item.ubicacion_gps}</span> : <span className="text-slate-400">-</span>
          )},
          { key: "activar_prelacion_extranet", header: "Prelación Extranet", render: (item) => (
            <Badge variant="outline" className={item.activar_prelacion_extranet ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-slate-50 text-slate-500 border-slate-200"}>
              {item.activar_prelacion_extranet ? "Sí" : "No"}
            </Badge>
          )},
          { key: "trimestre_publicidad", header: "Últ. Trim. Publicidad", render: (item) => (
            item.trimestre_publicidad && item.anio_publicidad ? (
              <span className="font-mono text-sm">{item.trimestre_publicidad} {item.anio_publicidad}</span>
            ) : (
              <span className="text-slate-400">-</span>
            )
          )},
          { key: "solvencia_status", header: "Solvencia Inmob.", render: (item) => {
            const solv = obligacionesMap.mapa_solvencias[item.id];
            if (!solv) return <Badge variant="outline" className="bg-slate-50 text-slate-500 border-slate-200 text-xs">Sin registro</Badge>;
            return (
              <Badge variant="outline" className={
                solv.estatus === "Solvente" 
                  ? "bg-green-50 text-green-700 border-green-200 text-xs" 
                  : "bg-red-50 text-red-700 border-red-200 text-xs"
              }>
                {solv.estatus}
              </Badge>
            );
          }},
          { key: "conformidad_status", header: "Conformidad Uso", render: (item) => {
            const conf = obligacionesMap.mapa_conformidades[item.id];
            if (!conf) return <Badge variant="outline" className="bg-slate-50 text-slate-500 border-slate-200 text-xs">Sin registro</Badge>;
            return (
              <Badge variant="outline" className={
                conf.estatus === "Solvente" 
                  ? "bg-green-50 text-green-700 border-green-200 text-xs" 
                  : "bg-red-50 text-red-700 border-red-200 text-xs"
              }>
                {conf.estatus}
              </Badge>
            );
          }},
          { key: "is_active", header: "Estado", render: (item) => (
            <Badge variant="outline" className={item.is_active !== false ? "bg-green-50 text-green-700 border-green-200" : "bg-red-50 text-red-700 border-red-200"}>
              {item.is_active !== false ? "Activo" : "Inactivo"}
            </Badge>
          )}
        ];
      case "sin-lae":
        return [
          { key: "nombre", header: "Nombre y apellido / Razón Social" },
          { key: "rif", header: "RIF o Cédula", render: (item) => (
            <span className="font-mono">{item.tipo_rif || "V"}-{item.rif}</span>
          )},
          { key: "domicilio_fiscal", header: "Domicilio Fiscal" },
          { key: "correo_electronico", header: "Correo" },
          { key: "is_active", header: "Estado", render: (item) => (
            <Badge variant="outline" className={item.is_active !== false ? "bg-green-50 text-green-700 border-green-200" : "bg-red-50 text-red-700 border-red-200"}>
              {item.is_active !== false ? "Activo" : "Inactivo"}
            </Badge>
          )}
        ];
      default:
        return [];
    }
  };

  // Export functions
  const getExportFileName = () => {
    return submodulo === "con-lae" ? "contribuyentes_con_lae" : "contribuyentes_sin_lae";
  };

  const getExportTitle = () => {
    return currentConfig?.title || "Contribuyentes";
  };

  const getExportData = () => {
    if (submodulo === "con-lae") {
      return filteredData.map(item => ({
        "Razón Social": item.nombre || "",
        "Denominación Comercial": item.denominacion_comercial || "",
        "RIF": `${item.tipo_rif || "J"}-${item.rif || ""}`,
        "N° LAE": item.numero_lae || "",
        "Tipo Licencia": item.tipo_licencia || "",
        "Tipo Contribuyente": item.tipo_contribuyente || "",
        "Correo": item.correo_electronico || "",
        "Celular": item.celular || "",
        "Estado": item.estado || "",
        "Municipio": item.municipio || "",
        "Parroquia": item.parroquia || "",
        "Domicilio Fiscal": item.domicilio_fiscal || "",
        "Código Catastral": item.codigo_catastral || "",
        "RIF Catastro": item.rif_catastro || "",
        "Propietario Catastro": item.propietario_catastro || "",
        "Ubicación GPS": item.ubicacion_gps || "",
        "Declara en Línea": item.declarar_en_linea ? "Sí" : "No",
        "Prelación Extranet": item.activar_prelacion_extranet ? "Sí" : "No",
        "Trim. Publicidad": item.trimestre_publicidad || "",
        "Año Publicidad": item.anio_publicidad || "",
        "Estatus": item.is_active !== false ? "Activo" : "Inactivo"
      }));
    } else {
      return filteredData.map(item => {
        return {
          "Nombre y apellido / Razón Social": item.nombre || "",
          "RIF o Cédula": `${item.tipo_rif || "V"}-${item.rif || ""}`,
          "Domicilio Fiscal": item.domicilio_fiscal || "",
          "Correo": item.correo_electronico || "",
          "Teléfono Local": item.telefono_local || "",
          "Celular": item.celular || "",
          "Estatus": item.is_active !== false ? "Activo" : "Inactivo"
        };
      });
    }
  };

  const exportToCSV = () => {
    const exportData = getExportData();
    if (exportData.length === 0) {
      toast.error("No hay datos para exportar");
      return;
    }
    const headers = Object.keys(exportData[0]).join(",");
    const rows = exportData.map(row => Object.values(row).map(v => `"${v}"`).join(",")).join("\n");
    const csv = `${headers}\n${rows}`;
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    saveAs(blob, `${getExportFileName()}_${new Date().toISOString().split('T')[0]}.csv`);
    toast.success("Exportado a CSV exitosamente");
  };

  const exportToJSON = () => {
    const exportData = getExportData();
    if (exportData.length === 0) {
      toast.error("No hay datos para exportar");
      return;
    }
    const json = JSON.stringify(exportData, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    saveAs(blob, `${getExportFileName()}_${new Date().toISOString().split('T')[0]}.json`);
    toast.success("Exportado a JSON exitosamente");
  };

  const exportToExcel = () => {
    const exportData = getExportData();
    if (exportData.length === 0) {
      toast.error("No hay datos para exportar");
      return;
    }
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, getExportTitle().substring(0, 31));
    XLSX.writeFile(wb, `${getExportFileName()}_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success("Exportado a Excel exitosamente");
  };

  const exportToPDF = () => {
    const exportData = getExportData();
    if (exportData.length === 0) {
      toast.error("No hay datos para exportar");
      return;
    }
    const doc = new jsPDF('landscape');
    doc.setFontSize(16);
    doc.text(`Listado de ${getExportTitle()}`, 14, 20);
    doc.setFontSize(10);
    doc.text(`Fecha: ${new Date().toLocaleDateString('es-VE')}`, 14, 28);
    
    const headers = Object.keys(exportData[0]);
    const rows = exportData.map(row => Object.values(row));
    
    autoTable(doc, {
      head: [headers],
      body: rows,
      startY: 35,
      styles: { fontSize: 7 },
      headStyles: { fillColor: [30, 64, 175] }
    });
    
    doc.save(`${getExportFileName()}_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success("Exportado a PDF exitosamente");
  };

  const exportToHTML = () => {
    const exportData = getExportData();
    if (exportData.length === 0) {
      toast.error("No hay datos para exportar");
      return;
    }
    const headers = Object.keys(exportData[0]);
    const title = getExportTitle();
    let html = `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <title>Listado de ${title}</title>
  <style>
    body { font-family: Arial, sans-serif; padding: 20px; }
    h1 { color: #1e40af; }
    table { border-collapse: collapse; width: 100%; margin-top: 20px; }
    th, td { border: 1px solid #ddd; padding: 10px; text-align: left; font-size: 12px; }
    th { background-color: #1e40af; color: white; }
    tr:nth-child(even) { background-color: #f9fafb; }
    tr:hover { background-color: #f3f4f6; }
    .fecha { color: #6b7280; margin-bottom: 20px; }
  </style>
</head>
<body>
  <h1>Listado de ${title}</h1>
  <p class="fecha">Fecha: ${new Date().toLocaleDateString('es-VE')}</p>
  <table>
    <thead>
      <tr>${headers.map(h => `<th>${h}</th>`).join("")}</tr>
    </thead>
    <tbody>
      ${exportData.map(row => `<tr>${Object.values(row).map(v => `<td>${v}</td>`).join("")}</tr>`).join("")}
    </tbody>
  </table>
</body>
</html>`;
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    saveAs(blob, `${getExportFileName()}_${new Date().toISOString().split('T')[0]}.html`);
    toast.success("Exportado a HTML exitosamente");
  };

  const exportToText = () => {
    const exportData = getExportData();
    if (exportData.length === 0) {
      toast.error("No hay datos para exportar");
      return;
    }
    const headers = Object.keys(exportData[0]);
    const colWidths = headers.map(h => Math.max(h.length, ...exportData.map(row => String(row[h]).length)));
    const title = getExportTitle().toUpperCase();
    
    let text = `LISTADO DE ${title}\n`;
    text += `Fecha: ${new Date().toLocaleDateString('es-VE')}\n`;
    text += "=".repeat(Math.max(colWidths.reduce((a, b) => a + b + 3, 0), 50)) + "\n\n";
    
    text += headers.map((h, i) => h.padEnd(colWidths[i])).join(" | ") + "\n";
    text += colWidths.map(w => "-".repeat(w)).join("-+-") + "\n";
    
    exportData.forEach(row => {
      text += headers.map((h, i) => String(row[h]).padEnd(colWidths[i])).join(" | ") + "\n";
    });
    
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    saveAs(blob, `${getExportFileName()}_${new Date().toISOString().split('T')[0]}.txt`);
    toast.success("Exportado a Texto exitosamente");
  };

  // If no submodulo selected, show landing page
  if (!submodulo || !currentConfig) {
    return (
      <div className="space-y-6" data-testid="contribuyentes-page">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center">
            <Users className="w-6 h-6 text-cyan-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Contribuyentes
            </h1>
            <p className="text-slate-500">Seleccione un submódulo del menú lateral</p>
          </div>
        </div>

        <Card>
          <CardContent className="p-8">
            <div className="text-center">
              <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-700 mb-2">Gestión de Contribuyentes</h3>
              <p className="text-slate-500 mb-6">
                Utilice el menú lateral para acceder a los diferentes tipos de contribuyentes.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-md mx-auto">
                {Object.entries(submoduloConfig).map(([key, config]) => {
                  const Icon = config.icon;
                  return (
                    <Button
                      key={key}
                      variant="outline"
                      className="flex flex-col items-center gap-2 h-auto py-6"
                      onClick={() => navigate(`/contribuyentes/${key}`)}
                      data-testid={`btn-${key}`}
                    >
                      <Icon className="w-8 h-8" />
                      <span className="text-sm">{config.title}</span>
                    </Button>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const Icon = currentConfig.icon;
  const columns = getTableColumns();

  return (
    <div className="space-y-6" data-testid="contribuyentes-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 bg-${currentConfig.color}-100 rounded-xl flex items-center justify-center`}>
            <Icon className={`w-6 h-6 text-${currentConfig.color}-600`} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {currentConfig.title}
            </h1>
            <p className="text-slate-500">Gestión de {currentConfig.title.toLowerCase()}</p>
          </div>
        </div>
      </div>

      {/* Content Card */}
      <Card>
        <CardContent className="p-4">
          {/* Toolbar */}
          <div className="flex items-center gap-2 flex-wrap mb-4">
              {/* Export Button */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="gap-2" data-testid="btn-exportar">
                    <Download className="w-4 h-4" />
                    Exportar
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem onClick={exportToHTML} className="gap-2 cursor-pointer">
                    <FileType className="w-4 h-4" />
                    HTML
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={exportToCSV} className="gap-2 cursor-pointer">
                    <FileText className="w-4 h-4" />
                    CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={exportToText} className="gap-2 cursor-pointer">
                    <File className="w-4 h-4" />
                    Texto
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={exportToExcel} className="gap-2 cursor-pointer">
                    <FileSpreadsheet className="w-4 h-4" />
                    Excel
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={exportToPDF} className="gap-2 cursor-pointer">
                    <FileText className="w-4 h-4" />
                    PDF
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={exportToJSON} className="gap-2 cursor-pointer">
                    <FileJson className="w-4 h-4" />
                    JSON
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              <Button 
                variant="outline" 
                onClick={fetchData} 
                disabled={loading}
                className="gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Actualizar
              </Button>
              
              {submodulo === "con-lae" && (
                <Button 
                  variant="outline" 
                  onClick={handleSincronizar} 
                  disabled={sincronizando}
                  className="gap-2 border-amber-300 text-amber-700 hover:bg-amber-50"
                  data-testid="btn-sincronizar-obligaciones"
                >
                  <RefreshCw className={`w-4 h-4 ${sincronizando ? 'animate-spin' : ''}`} />
                  Sincronizar Obligaciones
                </Button>
              )}

              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    className="bg-blue-800 hover:bg-blue-900 gap-2"
                    onClick={openCreateDialog}
                    data-testid="btn-nuevo"
                  >
                    <Plus className="w-4 h-4" />
                    Nuevo
                  </Button>
                </DialogTrigger>
                <DialogContent className={submodulo === "con-lae" ? "sm:max-w-2xl max-h-[90vh] overflow-y-auto" : "sm:max-w-md max-h-[90vh] overflow-y-auto"}>
                  <DialogHeader>
                    <DialogTitle>
                      {editingItem ? `Editar ${currentConfig.title}` : `Nuevo ${currentConfig.title}`}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="py-4">
                    {renderFormFields()}
                  </div>
                  <DialogFooter>
                    <DialogClose asChild>
                      <Button variant="outline">Cancelar</Button>
                    </DialogClose>
                    <Button 
                      className="bg-blue-800 hover:bg-blue-900"
                      onClick={editingItem ? handleUpdate : handleCreate}
                      data-testid="btn-guardar"
                    >
                      {editingItem ? "Actualizar" : "Guardar"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

            {/* Búsqueda servidor + Paginación */}
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex gap-1">
                <Input 
                  className="h-8 w-56 text-sm" 
                  placeholder="Buscar en servidor..." 
                  value={serverSearch}
                  onChange={(e) => setServerSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { setCurrentPage(0); fetchData(0, serverSearch, showAll); } }}
                  data-testid="server-search"
                />
                <Button variant="outline" size="sm" className="h-8 gap-1" onClick={() => { setCurrentPage(0); fetchData(0, serverSearch, showAll); }}>
                  <Search className="w-3.5 h-3.5" /> Buscar
                </Button>
              </div>

              {!showAll && (
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="sm" className="h-8 px-2" disabled={currentPage === 0}
                    onClick={() => { const p = currentPage - 1; setCurrentPage(p); fetchData(p, serverSearch, false); }}>
                    Anterior
                  </Button>
                  <span className="text-xs text-slate-600 px-2">
                    Pág. {currentPage + 1} de {Math.max(1, Math.ceil(totalRecords / pageSize))} | {totalRecords} registros
                  </span>
                  <Button variant="outline" size="sm" className="h-8 px-2" disabled={(currentPage + 1) * pageSize >= totalRecords}
                    onClick={() => { const p = currentPage + 1; setCurrentPage(p); fetchData(p, serverSearch, false); }}>
                    Siguiente
                  </Button>
                </div>
              )}
              {showAll && (
                <span className="text-xs text-slate-500">{data.length} de {totalRecords} registros (todos)</span>
              )}
              
              {/* Toggle Paginación / Todos */}
              <Button 
                variant={showAll ? "default" : "outline"}
                onClick={() => { 
                  const newShowAll = !showAll; 
                  setShowAll(newShowAll); 
                  setCurrentPage(0); 
                  fetchData(0, serverSearch, newShowAll); 
                }}
                className={`gap-2 ${showAll ? "bg-slate-700" : ""}`}
                data-testid="btn-toggle-all"
              >
                {showAll ? "Paginado" : "Todos"}
              </Button>
            </div>

              {/* Contador de registros */}
              {(submodulo === "con-lae" || submodulo === "sin-lae") && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-sm" data-testid="registro-counter">
                <span className="font-bold text-blue-800">{filteredData.length}</span>
                <span className="text-blue-600">de</span>
                <span className="font-bold text-blue-800">{data.length}</span>
                <span className="text-blue-600">registros</span>
              </div>
              )}

            {/* Barra de búsqueda (solo para otros submodulos) */}
            {submodulo !== "con-lae" && submodulo !== "sin-lae" && (
            <div className="flex items-center gap-2 max-w-sm">
              <Search className="w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-9"
                data-testid="search-input"
              />
            </div>
            )}
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Acciones
                  </th>
                  {columns.map((col, idx) => (
                    <th key={idx} className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      {col.header}
                    </th>
                  ))}
                </tr>
                {submodulo === "con-lae" && (
                <tr className="border-b border-slate-200 bg-slate-50/50">
                  <th className="px-4 py-1.5"></th>
                  <th className="px-4 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={conLaeFilters.nombre} onChange={(e) => setConLaeFilters({...conLaeFilters, nombre: e.target.value})} data-testid="filter-conlae-nombre" /></th>
                  <th className="px-4 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={conLaeFilters.denominacion} onChange={(e) => setConLaeFilters({...conLaeFilters, denominacion: e.target.value})} data-testid="filter-conlae-denominacion" /></th>
                  <th className="px-4 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={conLaeFilters.rif} onChange={(e) => setConLaeFilters({...conLaeFilters, rif: e.target.value})} data-testid="filter-conlae-rif" /></th>
                  <th className="px-4 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={conLaeFilters.numero_lae} onChange={(e) => setConLaeFilters({...conLaeFilters, numero_lae: e.target.value})} data-testid="filter-conlae-lae" /></th>
                  <th className="px-4 py-1.5">
                    <select className="h-7 w-full text-xs border rounded-md px-1 bg-white text-slate-700" value={conLaeFilters.tipo_licencia} onChange={(e) => setConLaeFilters({...conLaeFilters, tipo_licencia: e.target.value})} data-testid="filter-conlae-licencia">
                      <option value="todos">Todos</option>
                      <option value="Permanente">Permanente</option>
                      <option value="Transeúnte">Transeúnte</option>
                      <option value="Temporal">Temporal</option>
                      <option value="No Comercial">No Comercial</option>
                      <option value="Temporalmente sin Actividad">Temporalmente sin Actividad</option>
                      <option value="Sin Licencia">Sin Licencia</option>
                    </select>
                  </th>
                  <th className="px-4 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={conLaeFilters.actividades_eco} onChange={(e) => setConLaeFilters({...conLaeFilters, actividades_eco: e.target.value})} data-testid="filter-conlae-actividades" /></th>
                  <th className="px-4 py-1.5"></th>
                  <th className="px-4 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={conLaeFilters.codigo_catastral} onChange={(e) => setConLaeFilters({...conLaeFilters, codigo_catastral: e.target.value})} data-testid="filter-conlae-catastral" /></th>
                  <th className="px-4 py-1.5"></th>
                  <th className="px-4 py-1.5"></th>
                  <th className="px-4 py-1.5"></th>
                  <th className="px-4 py-1.5"></th>
                  <th className="px-4 py-1.5"></th>
                  <th className="px-4 py-1.5"></th>
                  <th className="px-4 py-1.5">
                    <select className="h-7 w-full text-xs border rounded-md px-1 bg-white text-slate-700" value={conLaeFilters.estado} onChange={(e) => setConLaeFilters({...conLaeFilters, estado: e.target.value})} data-testid="filter-conlae-estado">
                      <option value="todos">Todos</option>
                      <option value="activo">Activo</option>
                      <option value="inactivo">Inactivo</option>
                    </select>
                  </th>
                </tr>
                )}
                {submodulo === "sin-lae" && (
                <tr className="border-b border-slate-200 bg-slate-50/50">
                  <th className="px-4 py-1.5"></th>
                  <th className="px-4 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={sinLaeFilters.nombre} onChange={(e) => setSinLaeFilters({...sinLaeFilters, nombre: e.target.value})} data-testid="filter-sinlae-nombre" /></th>
                  <th className="px-4 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={sinLaeFilters.rif} onChange={(e) => setSinLaeFilters({...sinLaeFilters, rif: e.target.value})} data-testid="filter-sinlae-rif" /></th>
                  <th className="px-4 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={sinLaeFilters.domicilio} onChange={(e) => setSinLaeFilters({...sinLaeFilters, domicilio: e.target.value})} data-testid="filter-sinlae-domicilio" /></th>
                  <th className="px-4 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={sinLaeFilters.correo} onChange={(e) => setSinLaeFilters({...sinLaeFilters, correo: e.target.value})} data-testid="filter-sinlae-correo" /></th>
                  <th className="px-4 py-1.5">
                    <select className="h-7 w-full text-xs border rounded-md px-1 bg-white text-slate-700" value={sinLaeFilters.estado} onChange={(e) => setSinLaeFilters({...sinLaeFilters, estado: e.target.value})} data-testid="filter-sinlae-estado">
                      <option value="todos">Todos</option>
                      <option value="activo">Activo</option>
                      <option value="inactivo">Inactivo</option>
                    </select>
                  </th>
                </tr>
                )}
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={columns.length + 1} className="py-8 text-center">
                      <div className="flex items-center justify-center gap-2 text-slate-500">
                        <RefreshCw className="w-5 h-5 animate-spin" />
                        Cargando...
                      </div>
                    </td>
                  </tr>
                ) : filteredData.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length + 1} className="py-8 text-center text-slate-500">
                      No hay registros. Haga clic en "Nuevo" para agregar.
                    </td>
                  </tr>
                ) : (
                  filteredData.map((item, idx) => (
                    <tr key={item.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors" data-testid={`row-${idx}`}>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => openEditDialog(item)}
                            className="h-8 w-8 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                            data-testid={`edit-btn-${idx}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => handleDelete(item.id)}
                            className="h-8 w-8 text-red-600 hover:text-red-800 hover:bg-red-50"
                            data-testid={`delete-btn-${idx}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                      {columns.map((col, colIdx) => (
                        <td key={colIdx} className="py-3 px-4 text-sm text-slate-700">
                          {col.render ? col.render(item) : item[col.key]}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ContribuyentesPage;
